import { Sector } from 'recharts';
import type { NodeSummary } from '@/types/node';

/** Known vendor keywords → normalized display name */
const VENDOR_KEYWORDS: [string[], string][] = [
  [['advantech'], 'Advantech'],
  [['lanner'], 'Lanner'],
  [['dell'], 'Dell'],
  [['hp', 'hewlett'], 'HP'],
  [['lenovo'], 'Lenovo'],
  [['supermicro'], 'Supermicro'],
  [['amazon', 'aws', 'xen'], 'AWS'],
  [['microsoft', 'hyper-v'], 'Microsoft Hyper-V'],
  [['vmware'], 'VMware'],
  [['azure'], 'Azure'],
  [['google', 'gcp', 'gce'], 'Google Cloud'],
  [['qemu', 'kvm'], 'QEMU/KVM'],
  [['cisco'], 'Cisco'],
  [['intel'], 'Intel'],
  [['gigabyte'], 'Gigabyte'],
  [['asus'], 'ASUS'],
];

/** Corporate suffixes to strip when inferring vendor from raw string */
const CORP_SUFFIXES = /[,.]?\s*\b(Inc\.?|Corp\.?|Corporation|Ltd\.?|Limited|Electronics|Co\.?|LLC|GmbH|S\.?A\.?)\b\.?/gi;

/**
 * Clean a model string: strip internal annotations (^SUFFIX),
 * replace underscores with spaces, and trim whitespace.
 */
function cleanModel(raw: string): string {
  return raw
    .replace(/\^.*$/, '')       // strip ^SUFFIX annotations
    .replace(/_/g, ' ')         // underscores → spaces
    .trim();
}

/**
 * Parse vendor and model from a node's deviceVendorDetails.
 * Returns a normalized { vendor, model } for fine-grained display.
 */
export function parseHardware(node: NodeSummary): { vendor: string; model: string | null } {
  const details = node.deviceVendorDetails;

  if (!details || details === 'Unknown/Unknown') {
    if (node.profileName === 'Cloud Connector') return { vendor: 'Cloud (Virtual)', model: null };
    if (node.profileName === 'Virtual Edge') return { vendor: 'Virtual Machine', model: null };
    return { vendor: 'Unknown', model: null };
  }

  const lower = details.toLowerCase();

  // Check known vendor keywords first
  for (const [keywords, vendorName] of VENDOR_KEYWORDS) {
    if (keywords.some((kw) => lower.includes(kw))) {
      // Extract model from Vendor/Model format
      const slashIdx = details.indexOf('/');
      const model = slashIdx !== -1 ? cleanModel(details.substring(slashIdx + 1)) : null;
      return { vendor: vendorName, model: model || null };
    }
  }

  // Fallback: try Vendor/Model format parsing for unknown vendors
  const slashIdx = details.indexOf('/');
  if (slashIdx !== -1) {
    const rawVendor = details.substring(0, slashIdx).replace(CORP_SUFFIXES, '').trim();
    const model = cleanModel(details.substring(slashIdx + 1));
    return { vendor: rawVendor || details, model: model || null };
  }

  // Last resort: clean corporate suffixes from the whole string
  const cleaned = details.replace(CORP_SUFFIXES, '').trim();
  return { vendor: cleaned || details, model: null };
}

/**
 * Parse a normalized vendor name for grouping/charting.
 * Wraps parseHardware() for backward compatibility.
 */
export function parseVendor(node: NodeSummary): string {
  return parseHardware(node).vendor;
}

/**
 * Factory for Recharts pie/donut label renderers.
 * Positions labels outside the donut with proper text anchoring.
 * Labels for slices below `minPercent` are hidden (hover tooltip still works).
 *
 * @param minPercent  – hide labels below this fraction (default 0.04 = 4%)
 */
const RADIAN = Math.PI / 180;

export function createPieLabel(minPercent = 0.04) {
  return function renderLabel(props: Record<string, unknown>) {
    const { cx, cy, midAngle, outerRadius, name, value, percent } = props as {
      cx: number;
      cy: number;
      midAngle: number;
      outerRadius: number;
      name?: string;
      value?: number;
      percent?: number;
    };

    if (!percent || percent < minPercent) return null;

    const label = name ?? String(value ?? '');
    const radius = outerRadius + 22;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const displayName = label.length > 16 ? `${label.slice(0, 14)}…` : label;

    return (
      <text
        x={x}
        y={y}
        fill="currentColor"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {value != null ? `${displayName} (${value})` : displayName}
      </text>
    );
  };
}

/** Default pie label — hides slices below 8% */
export const renderPieLabel = createPieLabel(0.08);

/**
 * Active shape renderer for pie/donut charts.
 * Expands the hovered slice outward by 6px for a highlight effect.
 */
export function renderActiveShape(props: unknown) {
  const p = props as {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
  };

  return (
    <Sector
      cx={p.cx}
      cy={p.cy}
      innerRadius={p.innerRadius}
      outerRadius={p.outerRadius + 6}
      startAngle={p.startAngle}
      endAngle={p.endAngle}
      fill={p.fill}
    />
  );
}

export function getHealthScoreColor(score: number): string {
  if (score >= 90) return 'dls-text-status-online';
  if (score >= 70) return 'dls-text-status-warning';
  return 'dls-text-status-offline';
}

export function getUptimeColor(uptime: number): string {
  if (uptime >= 99) return 'dls-text-status-online';
  if (uptime >= 95) return 'dls-text-status-warning';
  return 'dls-text-status-offline';
}
